<template>
	<form-group
		:col-class="colClass"
		:label="label"
		:invalid="invalid">
		<div v-show="readonly" class="form-control-plaintext">{{ localValue }}</div>
		<div v-show="!readonly" class="address-autocomplete-wrap" ref="addressWrap">
			<field
				label=""
				:col-class="'stacked'"
				:value="localValue"
				v-on:input="onInput"
				:params="fieldParams"
				v-on:focus="onAddressFocus"
				v-on:blur="onAddressBlur">
			</field>
			<ul v-show="addressSuggestions.length > 0" class="list-group address-suggestions-list">
				<li
					v-for="(item, idx) in addressSuggestions"
					:key="idx"
					class="list-group-item list-group-item-action"
					v-on:mousedown.prevent="onSelectSuggestion(item.suggestion)">
					{{ item.label }}
				</li>
			</ul>
		</div>
	</form-group>
</template>
<script type="text/javascript">
import GoogleMapsLoader from "@/mixins/GoogleMapsLoader"
import Field from "./Field"
import FormGroup from "./FormGroup"

export default {
	name: "AutocompleteAddressField",
	mixins: [GoogleMapsLoader],
	components: {
		Field,
		FormGroup
	},
	props: {
		value: { type: String, default: "" },
		label: { type: String, default: "Address" },
		colClass: { type: String, default: "" },
		invalid: { type: [Boolean, String], default: false },
		readonly: { type: Boolean, default: false },
		placeholder: { type: String, default: "Start typing an address" }
	},
	data() {
		return {
			localValue: this.value || "",
			addressSuggestions: [],
			addressSuggestionsLoading: false,
			addressDebounceTimer: null,
			addressInputFocused: false,
			placesLib: null,
			autocompleteSessionToken: null
		}
	},
	computed: {
		fieldParams() {
			return {
				type: "text",
				placeholder: this.placeholder,
				autocomplete: "nope"
			}
		},
		apiKey() {
			return this.$store && this.$store.GoogleEmbedAPIkey ? this.$store.GoogleEmbedAPIkey : ""
		}
	},
	watch: {
		value(val) {
			this.localValue = val != null ? val : ""
		}
	},
	methods: {
		onInput(val) {
			this.localValue = val != null ? val : ""
			this.$emit("input", this.localValue)
			this.onAddressInput()
		},
		ensurePlacesLib() {
			var vm = this
			if (vm.placesLib) return Promise.resolve(vm.placesLib)
			if (!vm.apiKey) return Promise.reject(new Error("No API key"))
			return vm.loadGooglePlaces(vm.apiKey).then(function (lib) {
				vm.placesLib = lib
				if (!vm.autocompleteSessionToken && lib && lib.AutocompleteSessionToken) {
					vm.autocompleteSessionToken = new lib.AutocompleteSessionToken()
				}
				return lib
			})
		},
		suggestionDisplayText(suggestion) {
			if (!suggestion) return ""
			function str(val) {
				if (val == null) return ""
				if (typeof val === "string") return val
				if (typeof val === "function") return String(val())
				if (typeof val === "object" && val !== null) {
					if (val.string != null) return String(val.string)
					if (val.value != null) return String(val.value)
					if (val.text != null) return String(val.text)
				}
				return ""
			}
			function firstLongString(obj, minLen) {
				if (!obj || typeof obj !== "object") return ""
				minLen = minLen || 4
				try {
					if (obj.string != null && String(obj.string).length >= minLen) return String(obj.string)
					if (obj.value != null && String(obj.value).length >= minLen) return String(obj.value)
					if (obj.text != null && String(obj.text).length >= minLen) return String(obj.text)
					for (var k in obj) {
						if (Object.prototype.hasOwnProperty.call(obj, k)) {
							var v = obj[k]
							if (typeof v === "string" && v.length >= minLen) return v
							if (v && typeof v === "object" && v.string != null) return String(v.string)
						}
					}
				} catch (e) {}
				return ""
			}
			var pp = suggestion.placePrediction || suggestion
			if (pp) {
				var t = str(pp.text)
				if (t) return t
				var rawSf = pp.structuredFormat
				if (rawSf) {
					var main = str(rawSf.mainText)
					var sec = str(rawSf.secondaryText)
					if (main || sec) return [main, sec].filter(Boolean).join(", ")
				}
				if (typeof pp.toJSON === "function") {
					try {
						var j = pp.toJSON()
						if (j && j.text) { t = str(j.text); if (t) return t }
						if (j && j.structuredFormat) {
							var m = str(j.structuredFormat.mainText)
							var s = str(j.structuredFormat.secondaryText)
							if (m || s) return [m, s].filter(Boolean).join(", ")
						}
					} catch (e) {}
				}
				t = firstLongString(pp)
				if (t) return t
			}
			if (suggestion.text != null) return str(suggestion.text)
			if (suggestion.description != null) return str(suggestion.description)
			t = firstLongString(suggestion)
			if (t) return t
			return "Address"
		},
		onAddressInput() {
			var vm = this
			if (vm.addressDebounceTimer) clearTimeout(vm.addressDebounceTimer)
			vm.addressSuggestions = []
			var input = (vm.localValue || "").trim()
			if (input.length < 2) return
			vm.addressDebounceTimer = setTimeout(function () {
				vm.addressDebounceTimer = null
				if (!vm.addressInputFocused) return
				vm.fetchAddressSuggestions(input)
			}, 300)
		},
		onAddressFocus() {
			this.addressInputFocused = true
			var input = (this.localValue || "").trim()
			if (input.length >= 2 && this.addressSuggestions.length === 0) this.fetchAddressSuggestions(input)
		},
		onAddressBlur() {
			var vm = this
			vm.addressInputFocused = false
			if (vm.addressDebounceTimer) {
				clearTimeout(vm.addressDebounceTimer)
				vm.addressDebounceTimer = null
			}
			vm.addressSuggestions = []
		},
		fetchAddressSuggestions(input) {
			var vm = this
			vm.ensurePlacesLib().then(function (lib) {
				if (!vm.addressInputFocused) return
				if (!lib || !lib.AutocompleteSuggestion || !lib.AutocompleteSessionToken) return
				vm.addressSuggestionsLoading = true
				var request = {
					input: input,
					includedPrimaryTypes: ["street_address", "premise", "subpremise"],
					sessionToken: vm.autocompleteSessionToken || new lib.AutocompleteSessionToken()
				}
				lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then(function (result) {
					vm.addressSuggestionsLoading = false
					if (!vm.addressInputFocused) return
					var list = (result && result.suggestions) ? result.suggestions : []
					vm.addressSuggestions = list.slice(0, 5).map(function (s) {
						return { suggestion: s, label: vm.suggestionDisplayText(s) || "Address" }
					})
				}).catch(function () {
					vm.addressSuggestionsLoading = false
					vm.addressSuggestions = []
				})
			}).catch(function () {})
		},
		onSelectSuggestion(suggestion) {
			var vm = this
			if (!suggestion || !suggestion.placePrediction || !suggestion.placePrediction.toPlace) return
			var place = suggestion.placePrediction.toPlace()
			vm.addressSuggestions = []
			vm.autocompleteSessionToken = null
			if (vm.placesLib && vm.placesLib.AutocompleteSessionToken) {
				vm.autocompleteSessionToken = new vm.placesLib.AutocompleteSessionToken()
			}
			var fields = ["addressComponents", "formattedAddress", "location"]
			var fetchPromise = place.fetchFields ? place.fetchFields({ fields: fields }) : Promise.resolve()
			fetchPromise.then(function () {
				var data = (place && typeof place.toJSON === "function") ? place.toJSON() : place
				var raw = (data && (data.addressComponents || data.address_components)) || (place && (place.addressComponents || place.address_components))
				if (raw && Array.isArray(raw) && raw.length > 0) {
					var payload = vm.addressComponentsToPayload(raw)
					if (payload) {
						vm.localValue = payload.address || vm.localValue
						vm.$emit("input", vm.localValue)
						vm.$emit("address-selected", payload)
					}
					return
				}
				var addr = (data && data.formattedAddress) || (place && place.formattedAddress)
				addr = typeof addr === "string" ? addr : (addr && (addr.string != null ? addr.string : (addr.text != null ? addr.text : addr.value))) || ""
				if (addr && addr.length >= 5) {
					vm.loadGoogleGeocoding().then(function (lib) {
						var Geocoder = (lib && lib.Geocoder) || (window.google && window.google.maps && window.google.maps.Geocoder)
						if (!Geocoder) return
						var g = new Geocoder()
						g.geocode({ address: addr }, function (results, status) {
							if (status === "OK" && results && results[0] && results[0].address_components) {
								var payload = vm.addressComponentsToPayload(results[0].address_components)
								if (payload) {
									vm.localValue = payload.address || vm.localValue
									vm.$emit("input", vm.localValue)
									vm.$emit("address-selected", payload)
								}
							}
						})
					}).catch(function () {})
				} else {
					// No components and no formatted address: don't emit address-selected, so parent
					// keeps existing city/province/country/postal (matches original fillAddressFromPlace(place) with empty raw)
				}
			}).catch(function () {})
		},
		addressComponentsToPayload(raw) {
			if (!raw || !Array.isArray(raw)) return null
			function getText(val) {
				if (val == null) return ""
				if (typeof val === "string") return val
				if (typeof val === "object" && val.string != null) return val.string
				return ""
			}
			var components = raw.map(function (c) {
				var longName = c.long_name != null ? c.long_name : getText(c.longText)
				var shortName = c.short_name != null ? c.short_name : getText(c.shortText)
				var types = c.types || []
				if (!Array.isArray(types)) types = Array.from(types || [])
				return { long_name: longName, short_name: shortName, types: types }
			})
			function getComponent(types) {
				var comp = components.find(function (c) {
					return types.every(function (t) { return c.types.indexOf(t) >= 0 })
				})
				return comp || null
			}
			var streetNumberComp = getComponent(["street_number"])
			var routeComp = getComponent(["route"])
			var cityComp = getComponent(["locality"]) || getComponent(["postal_town"]) || getComponent(["sublocality", "sublocality_level_1"])
			var provinceComp = getComponent(["administrative_area_level_1"])
			var countryComp = getComponent(["country"])
			var postalComp = getComponent(["postal_code"])
			var streetNumber = streetNumberComp ? streetNumberComp.long_name : ""
			var route = routeComp ? routeComp.long_name : ""
			var address = [streetNumber, route].filter(Boolean).join(" ").trim() || this.localValue
			return {
				address: address,
				city: cityComp ? cityComp.long_name : "",
				province: provinceComp ? provinceComp.short_name : "",
				country: countryComp ? (countryComp.short_name || countryComp.long_name) : "",
				postal_code: postalComp ? postalComp.long_name : ""
			}
		}
	},
	mounted() {
		if (!this.readonly && this.apiKey) this.ensurePlacesLib().catch(function () {})
	},
	beforeDestroy() {
		if (this.addressDebounceTimer) clearTimeout(this.addressDebounceTimer)
	}
}
</script>
<style scoped>
.address-autocomplete-wrap {
	position: relative;
	width: 100%;
}
.address-suggestions-list {
	position: absolute;
	z-index: 1000;
	width: 100%;
	max-height: 240px;
	overflow-y: auto;
	margin: 0;
	padding: 0;
	list-style: none;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	border-radius: 0 0 4px 4px;
}
.address-suggestions-list .list-group-item {
	cursor: pointer;
	border-left: 1px solid rgba(0, 0, 0, 0.125);
	border-right: 1px solid rgba(0, 0, 0, 0.125);
}
.address-suggestions-list .list-group-item:first-child {
	border-top: none;
}
</style>
